import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, OnChanges, SimpleChanges, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../../features/auth/services/auth.service';
import { environment } from '../../../../environments/environment';
import * as L from 'leaflet';

declare var google: any;

@Component({
  selector: 'app-map',
  imports: [ HttpClientModule, CommonModule, MatIconModule],
  standalone: true,
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']  
})
export class MapComponent implements OnInit, AfterViewInit, OnChanges {

  @Input() width: string = '100%';
  @Input() height: string = '100%';
  @Input() showSearchInput: boolean = true;
  @Input() mode: string = 'default';
  @Input() updateCoordinates: { lat: number; lng: number } | null = null;
  @Input() useNewStructure: boolean = false;
  @Input() initialLat: number | null = null;
  @Input() initialLng: number | null = null;
  @Input() laboratories: any[] = [];
  @Input() fromMedicalCheckup: boolean = false;
  @Input() disableOpenStreetMap: boolean = false; // Nuevo input para deshabilitar OpenStreetMap
  autocompleteInitialized: boolean = false;
  

  
  isSearching: boolean = false;

  @ViewChild('addresstext') addresstext: any;
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;

  @Output() getAddress: EventEmitter<any> = new EventEmitter();
  @Output() getCordinate: EventEmitter<any> = new EventEmitter();
  @Output() getByAdreessByCordinate: EventEmitter<any> = new EventEmitter();
  @Output() laboratorySelected: EventEmitter<any> = new EventEmitter();
  @Output() markersLoaded: EventEmitter<boolean> = new EventEmitter();

  lat: number = 0;
  lon: number = 0;

  marker_location: any[] = [];

  googleMapsLoaded: boolean = false; // <-- FLAG para controlar carga del script de Google Places
  private map!: L.Map;
  private markers: L.Marker[] = [];
  private userMarker: L.CircleMarker | null = null;

  // Centro del mapa inicial
  center: { lat: number; lng: number } = { lat: 19.4326, lng: -99.1332 };
  zoom = 13;

  private apiKey: string | null = '';

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {
    this.apiKey = environment.apiGoogleMapsKey;
  }

  ngOnInit(): void {
    this.getLocation();
    this.waitForApiKey(); // Solo para Google Places Autocomplete
    
    if (this.updateCoordinates) {
      this.updateLocation(this.updateCoordinates.lat, this.updateCoordinates.lng);
    }
  }

  ngAfterViewInit(): void {
    this.initLeafletMap();
    
    // Agregar listener para redimensionamiento de ventana
    window.addEventListener('resize', () => {
      this.forceMapResize();
    });
    
    if (this.addresstext && this.addresstext.nativeElement) {
      const value = this.addresstext.nativeElement.value;
      if (value && value.length >= 7 && !this.autocompleteInitialized) {
        this.initAutocomplete();
        this.autocompleteInitialized = true;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['laboratories']) {
      if (this.laboratories && this.laboratories.length > 0) {
        setTimeout(() => {
          this.updateMarkersWithLaboratories();
        }, 100);
      }
    }
  }

  updateMarkersWithLaboratories() {
    if (this.laboratories && this.laboratories.length > 0) {
      const originMarker = this.marker_location.find(m => m.id === 'origin');
      
      this.marker_location = this.marker_location.filter(m => m.type !== 'laboratory');
      
      this.laboratories.forEach((lab) => {
        this.marker_location.push({
          lat: Number(lab.lat),
          lng: Number(lab.lng),
          title: lab.name,
          id: `lab_${lab.id}`,
          type: 'laboratory',
          laboratoryData: {
            id: lab.id,
            name: lab.name,
            address: lab.address,
            city: lab.city,
            state: lab.state,
            study: lab.study,
            lat: lab.lat,
            lng: lab.lng
          }
        });
      });
      
      if (this.fromMedicalCheckup && originMarker) {
        this.adjustMapViewForLaboratories(originMarker);
      }
      
      // Actualizar marcadores en Leaflet
      this.updateLeafletMarkers();
    }
  }
  
  private adjustMapViewForLaboratories(originMarker: any) {
    const allLats = [originMarker.lat, ...this.laboratories.map(lab => Number(lab.lat))];
    const allLngs = [originMarker.lng, ...this.laboratories.map(lab => Number(lab.lng))];
    
    const centerLat = (Math.min(...allLats) + Math.max(...allLats)) / 2;
    const centerLng = (Math.min(...allLngs) + Math.max(...allLngs)) / 2;
    
    this.center = { lat: centerLat, lng: centerLng };
    this.zoom = 14;
    
    // Actualizar vista del mapa de Leaflet
    if (this.map) {
      this.map.setView([centerLat, centerLng], this.zoom);
    }
  }

  initLeafletMap() {
    if (!this.mapContainer) return;

    // Inicializar mapa de Leaflet con tiles de OpenStreetMap
    this.map = L.map(this.mapContainer.nativeElement, {
      center: [this.center.lat, this.center.lng],
      zoom: this.zoom
    });

    // Agregar capa de tiles de OpenStreetMap (GRATUITO)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap contributors'
    }).addTo(this.map);

    // Configurar iconos personalizados
    this.setupCustomIcons();

    // Forzar redimensionamiento del mapa después de la inicialización
    setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize();
      }
    }, 100);

    // Agregar marcadores iniciales si existen
    if (this.marker_location.length > 0) {
      this.updateLeafletMarkers();
    }
  }

  setupCustomIcons() {
    // Fix para iconos por defecto de Leaflet
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    });
  }

  loadGoogleMapsScript() {
    // Solo cargar Google Maps para Places Autocomplete
    if (document.getElementById('googleMaps')) {
      this.googleMapsLoaded = true;
      return;
    }
    const script = document.createElement('script');
    script.id = 'googleMaps';
    if (!this.isValidApiKey(this.apiKey)) {
      console.error('API Key inválida');
      return;
    }
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(this.apiKey!)}&libraries=places`;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      this.googleMapsLoaded = true;
    };

    script.onerror = () => {
      console.error('Error al cargar Google Maps');
    };
  }


  waitForApiKey() {
    const interval = setInterval(() => {
      
      if (this.apiKey) {
        clearInterval(interval);
        this.loadGoogleMapsScript();
      }
    }, 300);
  }

  initAutocomplete() {
    if (!this.addresstext || !this.addresstext.nativeElement) {
      return;
    }

    if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
      return;
    }

    try {
      const autocomplete = new google.maps.places.Autocomplete(
        this.addresstext.nativeElement,
        {
          types: ['establishment', 'geocode'],
        }
      );

      google.maps.event.addListener(autocomplete, 'place_changed', () => {
      const place = autocomplete.getPlace();

      const typed = this.addresstext.nativeElement.value.trim();
      const referenceText = place.name || place.description || typed;

      if (referenceText.length < 7) {
        return;
      }

      if (!place || !place.geometry) {
        console.warn('Lugar inválido o sin geometría');
        return;
      }

      this.invokeEvent(place);
    });

      
    } catch (error) {
      console.error('Error inicializando autocompletado:', error);
    }
  }

  onSearchInput(event: any) {
      const value = event.target.value;
      this.isSearching = value && value.length >= 7;
      if (!this.autocompleteInitialized &&value.length >= 7 &&this.googleMapsLoaded && this.addresstext && this.addresstext.nativeElement) {
        this.initAutocomplete();
        this.autocompleteInitialized = true;
      }
  }

  clearSearch() {
    if (this.addresstext && this.addresstext.nativeElement) {
      this.addresstext.nativeElement.value = '';
      this.isSearching = false;
    }
  }

  invokeEvent(place: any) {
    this.isSearching = false;
    
    const data = JSON.parse(JSON.stringify(place));
    this.getAddress.emit(data);
    this.changeMapLocation(data.geometry.location.lat, data.geometry.location.lng);

    // Obtener dirección para llenar campos del formulario (excepto en medical-checkup)
    if (!this.fromMedicalCheckup) {
      // Enviar información del lugar desde Google Places
      this.getByAdreessByCordinate.emit(data);
      
      // Solo obtener datos complementarios de OpenStreetMap si NO está deshabilitado
      if (!this.disableOpenStreetMap) {
        this.getAddressCoordinate({ 
          lat: data.geometry.location.lat, 
          lng: data.geometry.location.lng, 
          title: data.name || 'Ubicación seleccionada' 
        });
      } else {
        console.log('OpenStreetMap deshabilitado - solo usando datos de Google Places');
      }
    }
  }

  changeMapLocation(newLat: number, newLng: number) {
    this.center = { lat: newLat, lng: newLng };
    if (this.fromMedicalCheckup && this.laboratories.length === 0) {
      this.zoom = 14;
    }
    
    // Actualizar centro del mapa de Leaflet
    if (this.map) {
      this.map.setView([newLat, newLng], this.zoom);
    }
    
    // Forzar recreación completa de marcadores
    this.marker_location = [];
    
    let body = { 
      lat: newLat, 
      lng: newLng, 
      title: 'Ubicación seleccionada', 
      id: 'origin_' + Date.now(),
      type: this.fromMedicalCheckup ? 'user-location' : 'origin'
    };
    this.marker_location.push(body);
    
    if (this.laboratories && this.laboratories.length > 0) {
      this.laboratories.forEach(lab => {
        this.marker_location.push({
          lat: Number(lab.lat),
          lng: Number(lab.lng),
          title: lab.name,
          id: `lab_${lab.id}`,
          type: 'laboratory',
          laboratoryData: {
            id: lab.id,
            name: lab.name,
            address: lab.address,
            city: lab.city,
            state: lab.state,
            study: lab.study,
            lat: lab.lat,
            lng: lab.lng
          }
        });
      });
    }
    
    // Actualizar marcadores en Leaflet
    this.updateLeafletMarkers();
  }

  updateLocation(lat: number, lng: number, title: string = 'Ubicación seleccionada') {
    // Forzar actualización completa usando changeMapLocation
    this.changeMapLocation(lat, lng);
  }

  reinitializeAutocomplete() {
    setTimeout(() => {
      this.initAutocomplete();
    }, 100);
  }

   getLocation(): void {
      if (this.mode === 'view' && this.initialLat && this.initialLng) {
        // Modo view: usar coordenadas iniciales proporcionadas
        this.changeMapLocation(this.initialLat, this.initialLng);
        this.zoom = 14; // Zoom apropiado para mostrar laboratorios cercanos
      } else if (this.mode === 'create'){
        // Solo usar geolocalización si no hay laboratorios (no viene desde medical checkup)
        if (this.laboratories.length === 0) {
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
              const lat = position.coords.latitude;
              const lng = position.coords.longitude;
              this.changeMapLocation(lat, lng);
              // Solo obtener dirección automáticamente si OpenStreetMap NO está deshabilitado
              if (!this.disableOpenStreetMap) {
                this.getAddressCoordinate({ lat, lng, title: 'Ubicación actual' });
              } else {
                console.log('OpenStreetMap deshabilitado - no obteniendo dirección automática');
              }
            });
          } else {
            console.log("No support for geolocation");
          }   
        }
      }else{
        if(this.mode=='update'){
           const profileDataStr = localStorage.getItem('profileData');
            const direccionStr = sessionStorage.getItem('direccionParaEditar');

            if (profileDataStr && direccionStr) {
              const profileData = JSON.parse(profileDataStr);
              const addressList = profileData.address || [];

              const direccionParaEditar = JSON.parse(direccionStr);
              const idAddress = direccionParaEditar.idAddress;

              // Buscar la dirección  por ID
              const direccion = addressList.find((a: any) => a.idAddress === idAddress);

              if (direccion) {
                const lat = direccion.lat;
                const lng = direccion.lng;

                if (lat && lng) {
                  this.changeMapLocation(lat, lng); 
                } else {
                  console.log("La dirección no tiene coordenadas.");
                }
              } else {
                console.log(`No se encontró dirección con idAddress = ${idAddress}`);
              }

            } else {
              console.log("No hay datos en localStorage o sessionStorage.");
            }
         
        }
      }

  }
  updateLeafletMarkers() {
    if (!this.map) return;

    // Limpiar marcadores existentes
    this.markers.forEach(marker => marker.remove());
    this.markers = [];
    if (this.userMarker) {
      this.userMarker.remove();
      this.userMarker = null;
    }

    // Agregar nuevos marcadores
    this.marker_location.forEach(location => {
      if (location.type === 'user-location') {
        // Círculo azul para ubicación de usuario en medical-checkup
        this.userMarker = L.circleMarker([location.lat, location.lng], {
          radius: 10,
          fillColor: '#1976D2',
          fillOpacity: 0.8,
          color: '#0D47A1',
          weight: 3
        }).addTo(this.map);
        this.userMarker.bindPopup(location.title);
      } else {
        // Marcador normal
        const marker = L.marker([location.lat, location.lng], {
          draggable: this.mode !== 'view' && location.type !== 'laboratory'
        }).addTo(this.map);
        
        marker.bindPopup(location.title);
        
        // Evento de arrastre
        if (this.mode !== 'view' && location.type !== 'laboratory') {
          marker.on('dragend', (event: any) => {
            const position = event.target.getLatLng();
            const body_cordinates = { 
              lat: position.lat, 
              lng: position.lng, 
              title: location.title 
            };
            this.getCordinate.emit(body_cordinates);
            this.getAddressCoordinate(body_cordinates);
          });
        }
        
        // Evento de click para laboratorios
        if (location.type === 'laboratory') {
          marker.on('click', () => {
            this.laboratorySelected.emit(location);
          });
        }
        
        this.markers.push(marker);
      }
    });
  }

  getAddressCoordinate(body: any) {
    // Siempre obtener datos de Google Maps API para dirección completa (PAGO)
    this.getAddressFromGoogle(body);
    
    // Solo obtener datos de OpenStreetMap si NO está deshabilitado
    if (!this.disableOpenStreetMap) {
      // Obtener datos de OpenStreetMap para código postal, municipio y ciudad (GRATUITO)
      const osrmUrl = `https://nominatim.openstreetmap.org/reverse?lat=${body.lat}&lon=${body.lng}&format=json`;
      this.http.get<any>(osrmUrl).subscribe({
        next: (data) => {
          if (data && data.address) {
            // Enviar datos de OpenStreetMap con campos específicos para complementar
            const osmData = {
              address: data.address,
              display_name: data.display_name,
              isOpenStreetMap: true // Marcar como datos de OSM
            };
            this.getByAdreessByCordinate.emit(osmData);
          }
        },
        error: (err) => {
          console.error('Error obteniendo datos de OpenStreetMap:', err);
        }
      });
    } else {
      console.log('OpenStreetMap deshabilitado - solo usando Google Maps API');
    }
  }

  private isValidApiKey(key: string | null): boolean {
    if (!key || typeof key !== 'string') return false;
    const apiKeyRegex = /^[A-Za-z0-9_-]{39}$/;
    return apiKeyRegex.test(key);
  }

  getAddressFromGoogle(body: any) {
    if (!this.isValidApiKey(this.apiKey)) {
      console.error('API Key inválida para geocoding');
      return;
    }
    const googleUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${body.lat},${body.lng}&key=${encodeURIComponent(this.apiKey!)}`;
    this.http.get<any>(googleUrl).subscribe({
      next: (response) => {
        if (response.status === 'OK' && response.results.length > 0) {
          const address = response.results[0];
          this.getByAdreessByCordinate.emit(address);
          console.log('ADRESSSSSSSSSSSSSSSSSSSSSSSSSS');
          console.log(address);
        }
      },
      error: (err) => {
        console.error("geocoding con Google:", err);
      }
    });
  }

  trackByLocation(index: number, location: any) {
    return location.id;
  }

  forceMapResize() {
    if (this.map) {
      setTimeout(() => {
        this.map.invalidateSize(true);
        // Forzar re-renderizado del mapa
        this.map.getContainer().style.height = this.height;
        this.map.getContainer().style.width = this.width;
        this.map.invalidateSize(true);
      }, 100);
    }
  }

  forceUpdateMarkers() {
    this.updateMarkersWithLaboratories();
  }

  onMarkerClick(location: any) {
    if (location.type === 'laboratory') {
      this.laboratorySelected.emit(location);
    }
  }

  updateLocationAndLaboratories(lat: number, lng: number, laboratories: any[]) {
    this.laboratories = laboratories || [];
    this.center = { lat, lng };
    
    this.marker_location = [];
    
    if (this.fromMedicalCheckup) {
      this.zoom = this.laboratories.length > 0 ? 14 : 15;
    }
    
    if (this.laboratories.length > 0) {
      this.laboratories.forEach((lab) => {
        this.marker_location.push({
          lat: Number(lab.lat),
          lng: Number(lab.lng),
          title: lab.name,
          id: `lab_${lab.id}`,
          type: 'laboratory',
          laboratoryData: {
            id: lab.id,
            name: lab.name,
            address: lab.address,
            city: lab.city,
            state: lab.state,
            study: lab.study,
            lat: lab.lat,
            lng: lab.lng
          }
        });
      });
    }
    
    // Actualizar mapa de Leaflet
    if (this.map) {
      this.map.setView([lat, lng], this.zoom);
      this.updateLeafletMarkers();
    }
    
    setTimeout(() => {
      this.markersLoaded.emit(true);
    }, 100);
  }

}
